import axios from "axios";
import Qs from "qs";
import { Environment } from "../shared/core/Environment";
import { RequestClass } from "./core/olga/RequestClass";

export default class OcdpAPI {
  client = null;
  environment_id = null;
  token = null;
  static requestStates = new Map();
  static abortControllers = new Map();
  static cancel_sources = {};

  constructor() {
    this.client = new RequestClass(
      process.env.REACT_APP_SERVICES_ENDPOINT_BASE
    );
    this.environment_id = Environment.id;
    this.token = Environment.token;
  }

  async query(schema, options) {
    const { brand_id, unit_id, limit, sort = "LAST_ORDER_DESC" } = options;
    const response = await this.client.post({
      service: "environments",
      endpoint: "/ocdp/query",
      ignoreVersion: true,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      data: {
        environment_id: this.environment_id,
        brand_id,
        unit_id,
        schema,
        sort,
        limit,
      },
    });

    return response.getData({ execution_id: null });
  }

  async status(execution_id) {
    const response = await this.client.get({
      service: "environments",
      endpoint: `/ocdp/status/${execution_id}`,
      ignoreVersion: true,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    return response.getData({ status: "ERROR" }).status || "ERROR";
  }

  async export_status(job_id) {
    const response = await this.client.get({
      service: "environments",
      endpoint: `/ocdp/export/${job_id}/status`,
      ignoreVersion: true,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    return response.getData({ status: "ERROR" }).status || "ERROR";
  }

  async stats(execution_id, { brand_id, unit_id, filters = {} }) {
    const requestKey = "stats";

    if (OcdpAPI.isRequestInProgress(requestKey)) {
      await this.cancelRequest(requestKey); // Cancela a requisição atual
    }

    const controller = new AbortController();
    const signal = controller.signal;
    OcdpAPI.abortControllers.set(requestKey, controller);

    OcdpAPI.setRequestState(requestKey, true);

    try {
      const query_string = Qs.stringify(
        { brand_id, unit_id, filters },
        {
          addQueryPrefix: false,
          arrayFormat: "indices",
        }
      );

      const response = await this.client.get({
        service: "environments",
        endpoint: `/ocdp/stats/${execution_id}?${query_string}`,
        ignoreVersion: true,
        signal,
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
      return response.getData({});
    } catch (err) {
      return {};
    } finally {
      OcdpAPI.setRequestState(requestKey, false);
      OcdpAPI.abortControllers.delete(requestKey);
    }
  }

  async fetch(
    execution_id,
    { brand_id, unit_id, filters = {}, page = 0, size = 200, term = null }
  ) {
    const requestKey = "fetch";

    if (OcdpAPI.isRequestInProgress(requestKey)) {
      await this.cancelRequest(requestKey); // Cancela a requisição atual
    }

    const controller = new AbortController();
    const signal = controller.signal;
    OcdpAPI.abortControllers.set(requestKey, controller);

    OcdpAPI.setRequestState(requestKey, true);

    try {
      const params = {
        brand_id,
        unit_id,
        term: term,
        filters: filters || {},
        page_number: Math.max(0, page),
        page_size: size,
      };

      const query_string = Qs.stringify(params, {
        addQueryPrefix: false,
        arrayFormat: "indices",
      });

      const response = await this.client.get({
        service: "environments",
        endpoint: `/ocdp/fetch/${execution_id}?${query_string}`,
        ignoreVersion: true,
        signal,
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });

      return response.getData({
        term_occurrences: 0,
        page_number: page,
        page_size: size,
        page_items: [],
      });
    } catch (err) {
      if (err.name === "AbortError") {
        console.log(`Requisição "${requestKey}" foi abortada.`);
      } else {
        console.error("Erro durante a requisição:", err);
      }

      return {
        term_occurrences: 0,
        page_number: page,
        page_size: size,
        page_items: [],
      };
    } finally {
      OcdpAPI.setRequestState(requestKey, false);
      OcdpAPI.abortControllers.delete(requestKey);
    }
  }

  async infos(customer_id, { brand_id, unit_id }) {
    const response = await this.client.get({
      service: "environments",
      endpoint: `/ocdp/data/${customer_id}/infos`,
      params: {
        brand_id,
        unit_id,
      },
      ignoreVersion: true,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    return response.getData({});
  }

  async orders(customer_id, { brand_id, unit_id, page = 0, size = 18 }) {
    const response = await this.client.get({
      service: "environments",
      endpoint: `/ocdp/data/${customer_id}/orders`,
      ignoreVersion: true,
      params: {
        brand_id,
        unit_id,
        page: Math.max(0, page),
        limit: Math.max(200, size),
      },
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    return response.getData({});
  }

  async events(customer_id, { brand_id, unit_id, page = 0, size = 18 }) {
    const response = await this.client.get({
      service: "environments",
      endpoint: `/ocdp/data/${customer_id}/events`,
      ignoreVersion: true,
      params: {
        brand_id,
        unit_id,
        page: Math.max(0, page),
        limit: Math.max(200, size),
      },
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    return response.getData({});
  }

  async audiences() {
    const response = await this.client.get({
      service: "environments",
      endpoint: "/ocdp/audiences",
      ignoreVersion: true,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    return response.getData([]);
  }

  async audience(audience_id) {
    const response = await this.client.get({
      service: "environments",
      endpoint: `/ocdp/audiences/${audience_id}`,
      ignoreVersion: true,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    return response.getData(null);
  }

  async upsert_audience(
    audience_id,
    {
      brand_id,
      unit_id,
      name,
      type,
      filters,
      boxes,
      links,
      schema,
      sort,
      limit,
    }
  ) {
    const params = {
      service: "environments",
      endpoint: "/ocdp/audiences",
      ignoreVersion: true,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      data: {
        brand_id,
        unit_id,
        name: name,
        type: type,
        filters: filters,
        schema: schema,
        boxes: boxes,
        links: links,
        sort: sort,
        limit: limit,
      },
    };

    if (audience_id) {
      params.endpoint += `/${audience_id}`;
      const response = await this.client.put(params);
      return response.getData({ audience_id: null, execution_id: null });
    }
    const response = await this.client.post(params);
    return response.getData({ audience_id: null, execution_id: null });
  }

  async delete_audience(audience_id) {
    const response = await this.client.delete({
      service: "environments",
      endpoint: `/ocdp/audiences/${audience_id}`,
      ignoreVersion: true,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    return response.getData(null);
  }

  async export(execution_id, audience_id = null, filters = []) {
    const response = await this.client.post({
      service: "environments",
      endpoint: "/ocdp/export",
      ignoreVersion: true,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      data: {
        execution_id,
        audience_id,
        filters,
      },
    });

    return response.getData({ job_id: null, link: null });
  }

  static setRequestState(key, state) {
    OcdpAPI.requestStates.set(key, state);
  }

  static isRequestInProgress(key) {
    return OcdpAPI.requestStates.get(key) === true;
  }

  async cancelRequest(key) {
    const controller = OcdpAPI.abortControllers.get(key);
    if (controller) {
      controller.abort();
      OcdpAPI.setRequestState(key, false);
      OcdpAPI.abortControllers.delete(key);
      await new Promise((resolve) => setTimeout(resolve, 0));
    } else {
      console.log(`Nenhuma requisição "${key}" para cancelar.`);
    }
  }
}
